/**
 * Browser-engine detection + per-signature strategy cache.
 *
 * Different rendering engines have different SVG-text fidelity, foreignObject
 * support, and kerning behaviour:
 *   - Chromium  → tightest SVG-text fidelity, foreignObject reliable
 *   - WebKit    → SVG-text drifts on tightly tracked typography; foreignObject
 *                 can escape clipping bounds without explicit clip-path
 *   - Gecko     → SVG-text is close to HTML; foreignObject reliable
 *
 * Heuristics use the engine to pick safer defaults — e.g. WebKit gets a
 * stronger bias toward foreignObject for any non-trivial typography.
 *
 * Strategy cache keys by an element signature so once a (font, weight,
 * style, variation, script, engine) tuple has been proven to render
 * cleanly with strategy X, subsequent exports skip the retry escalation
 * entirely. Cache lives for the page lifetime; storage is in-memory.
 */

const ENGINE = (() => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/firefox|fxios/i.test(ua)) return 'gecko';
  if (/edg/i.test(ua)) return 'chromium';
  if (/chrome|chromium|crios/i.test(ua)) return 'chromium';
  if (/safari/i.test(ua) && !/chrome|chromium|edge/i.test(ua)) return 'webkit';
  return 'unknown';
})();

export function getBrowserEngine() { return ENGINE; }

// Coarse script classifier — used in the signature so different scripts
// don't share cached strategies. Cheap (single-codepoint scan).
function detectScriptClass(text) {
  if (!text) return 'empty';
  for (let i = 0; i < text.length; i++) {
    const cp = text.codePointAt(i);
    if (cp == null) continue;
    if (cp >= 0x0590 && cp <= 0x05FF) return 'hebrew';
    if (cp >= 0x0600 && cp <= 0x06FF) return 'arabic';
    if (cp >= 0x0900 && cp <= 0x097F) return 'devanagari';
    if (cp >= 0x4E00 && cp <= 0x9FFF) return 'cjk-han';
    if (cp >= 0x3040 && cp <= 0x30FF) return 'kana';
    if (cp >= 0xAC00 && cp <= 0xD7AF) return 'hangul';
    if (cp >= 0x0400 && cp <= 0x04FF) return 'cyrillic';
    if (cp >= 0x0370 && cp <= 0x03FF) return 'greek';
    if (cp > 0x024F) return 'extended';
  }
  return 'latin';
}

export function computeTextSignature(liveEl, cs) {
  if (!liveEl || !cs) return '';
  return [
    cs.fontFamily            || '',
    cs.fontWeight            || '',
    cs.fontStyle             || '',
    cs.fontVariationSettings || 'normal',
    cs.fontFeatureSettings   || 'normal',
    cs.writingMode           || 'horizontal-tb',
    cs.direction             || 'ltr',
    detectScriptClass(liveEl.textContent || ''),
    ENGINE,
  ].join('|');
}

// In-memory cache; max ~200 entries (typical design has <50 text elements
// and signatures repeat across them — cap is a memory safety, not a logic
// one). LRU-ish eviction: oldest insertion order, drop first when over cap.
const CACHE_MAX = 200;
const strategyCache = new Map();

export function getCachedStrategy(signature) {
  if (!signature) return null;
  return strategyCache.get(signature) || null;
}

export function setCachedStrategy(signature, strategy) {
  if (!signature || !strategy) return;
  if (strategyCache.size >= CACHE_MAX && !strategyCache.has(signature)) {
    const firstKey = strategyCache.keys().next().value;
    if (firstKey) strategyCache.delete(firstKey);
  }
  strategyCache.set(signature, strategy);
}

export function clearStrategyCache() { strategyCache.clear(); }
export function getStrategyCacheSize() { return strategyCache.size; }

/**
 * Engine-aware default strategy. Called by pickTextStrategy AFTER its
 * manual-override checks and BEFORE its content heuristics. Returns a
 * strategy name if the engine has a stronger default than svg-word,
 * else null (caller falls through to its own auto-heuristics).
 */
export function engineDefaultStrategy(cs, scriptClass) {
  // WebKit's SVG <text> kerning drifts from its HTML <text> rendering on
  // tracked typography. If letter-spacing is non-zero AND we're on WebKit,
  // foreignObject keeps the HTML engine in charge end-to-end.
  if (ENGINE === 'webkit') {
    const ls = cs.letterSpacing;
    if (ls && ls !== 'normal' && parseFloat(ls) !== 0) {
      return 'foreignObject';
    }
  }
  // Any non-Latin script: foreignObject regardless of engine.
  if (scriptClass !== 'latin' && scriptClass !== 'empty') {
    return 'foreignObject';
  }
  return null;
}
